import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api, DISCIPLINES, GEOGRAPHIES, MODELS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Search, Compass, ExternalLink, CalendarClock, Coins, Globe, Bell, BellRing, ScanSearch, Download, Briefcase, Building2, RefreshCw, Trash2, Plus, Loader2, SlidersHorizontal } from "lucide-react";
import NotificationDetail from "@/components/NotificationDetail";

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  const diff = Math.ceil((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  return diff;
}

export default function Funding() {
  const [q, setQ] = useState({ discipline: "Sciences", geography: "India", keywords: "", modelIdx: 0 });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [detailItem, setDetailItem] = useState(null);

  // Client-side filter/sort state
  const [filter, setFilter] = useState("");
  const [sortBy, setSortBy] = useState("default"); // default | deadline | agency | amount
  const [geoTab, setGeoTab] = useState("all");     // all | india | intl
  const [categorySel, setCategorySel] = useState(new Set());   // empty = all
  const [funderScope, setFunderScope] = useState("all");        // all | public | private
  const [deadlineFilter, setDeadlineFilter] = useState("all");  // all | open | closing_soon
  const [budgetRange, setBudgetRange] = useState([0, 1_000_000_000]); // INR
  const [currency, setCurrency] = useState("INR");
  const [showFilters, setShowFilters] = useState(false);

  const loadAlerts = () => api.get("/funding/alerts").then(r => setAlerts(r.data));

  // Instant curated hydrate on mount + on any filter param change (fast, no LLM).
  const loadCurated = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/funding/curated", { params: { geography: q.geography, discipline: q.discipline }});
      setItems(data.items || []);
    } catch {
      setItems([]);
    } finally { setLoading(false); }
  };
  useEffect(() => { loadAlerts(); }, []);
  useEffect(() => { loadCurated(); }, [q.geography, q.discipline]);

  // "Refresh live" — hit the AI scout to enrich with fresh opportunities.
  const refreshLive = async () => {
    setRefreshing(true);
    const toastId = toast.loading("Fetching live grant calls… (up to 45s)", { duration: 60000 });
    try {
      const m = MODELS[q.modelIdx];
      const { data } = await api.post("/funding/search", {
        discipline: q.discipline, geography: q.geography, keywords: q.keywords,
        model_provider: m.provider, model_name: m.name,
      });
      setItems(data.items || []);
      toast.dismiss(toastId);
      const live = (data.items || []).filter(x => x.source === "live").length;
      if (data.warning) toast.warning(`${data.items?.length || 0} in feed · live scout unavailable`);
      else toast.success(`Refreshed — ${live} fresh + ${(data.items?.length || 0) - live} curated`);
    } catch (e) {
      toast.dismiss(toastId);
      toast.error(e?.message || "Live refresh failed — showing curated");
    } finally { setRefreshing(false); }
  };

  const saveAlert = async (it) => {
    try {
      await api.post("/funding/alerts", {
        name: it.name, agency: it.agency, geography: it.geography, discipline: it.discipline,
        deadline: it.deadline || "", amount: it.amount || "", url: it.url || "", summary: it.summary || "",
      });
      toast.success("Alert saved. We'll surface it as the deadline approaches.");
      loadAlerts();
    } catch { toast.error("Could not save alert"); }
  };

  const deleteAlert = async (id) => {
    await api.delete(`/funding/alerts/${id}`);
    loadAlerts();
  };

  // Amount parser (INR-aware). Returns numeric INR value from strings like "INR up to 50 L", "USD 250k/yr".
  const parseAmtToInr = (s) => {
    if (!s) return 0;
    const str = String(s);
    let usdRate = 84, eurRate = 92, gbpRate = 106, audRate = 55, cadRate = 62, jpyRate = 0.57, zarRate = 4.5;
    const m = str.match(/([\d,.]+)\s*(Cr|Crore|Lakh|Lakhs|L|M|Mn|Bn|B|k|K)?/i);
    if (!m) return 0;
    let n = parseFloat(m[1].replace(/,/g,"")) || 0;
    const u = (m[2] || "").toLowerCase();
    if (u === "cr" || u === "crore") n *= 10000000;
    else if (u === "l" || u === "lakh" || u === "lakhs") n *= 100000;
    else if (u === "m" || u === "mn") n *= 1000000;
    else if (u === "b" || u === "bn") n *= 1000000000;
    else if (u === "k") n *= 1000;
    const up = str.toUpperCase();
    if (up.includes("USD") || up.includes("$")) n *= usdRate;
    else if (up.includes("EUR") || up.includes("€")) n *= eurRate;
    else if (up.includes("GBP") || up.includes("£")) n *= gbpRate;
    else if (up.includes("AUD")) n *= audRate;
    else if (up.includes("CAD")) n *= cadRate;
    else if (up.includes("JPY") || up.includes("¥")) n *= jpyRate;
    else if (up.includes("ZAR")) n *= zarRate;
    return n;
  };
  const inrToDisplay = (inr) => {
    if (currency === "USD") return `US$ ${Math.round(inr/84).toLocaleString()}`;
    if (currency === "EUR") return `€ ${Math.round(inr/92).toLocaleString()}`;
    if (inr >= 10000000) return `₹ ${(inr/10000000).toFixed(1)} Cr`;
    if (inr >= 100000) return `₹ ${(inr/100000).toFixed(1)} L`;
    return `₹ ${Math.round(inr).toLocaleString()}`;
  };

  const CATEGORIES = [
    "Research Grant","Fellowship","Conference","Workshop","FDP",
    "High Risk High Reward","Bilateral","College / Department Grant",
    "Sports","Library","Cultural","Entrepreneurship","Travel Grant","Seminar",
  ];
  const toggleCat = (c) => {
    setCategorySel(prev => {
      const next = new Set(prev);
      next.has(c) ? next.delete(c) : next.add(c);
      return next;
    });
  };

  // Derived: filtered + sorted list
  const visible = (() => {
    const f = filter.trim().toLowerCase();
    let out = items.filter(it => {
      if (geoTab === "india" && !(it.geography || "").toLowerCase().includes("india")) return false;
      if (geoTab === "intl"  &&  (it.geography || "").toLowerCase().includes("india")) return false;
      if (categorySel.size && !categorySel.has(it.category)) return false;
      if (funderScope === "public"  && it.funder_scope === "Private") return false;
      if (funderScope === "private" && it.funder_scope !== "Private") return false;
      if (deadlineFilter !== "all") {
        const d = daysUntil(it.deadline);
        if (deadlineFilter === "open" && (d !== null && d < 0)) return false;
        if (deadlineFilter === "closing_soon" && (d === null || d > 30 || d < 0)) return false;
      }
      // Budget filter
      const inr = parseAmtToInr(it.amount);
      if (inr > 0) {
        if (inr < budgetRange[0] || inr > budgetRange[1]) return false;
      }
      if (!f) return true;
      return [it.name, it.agency, it.summary, it.discipline, it.geography, it.category].some(v => (v || "").toLowerCase().includes(f));
    });
    if (sortBy === "deadline") {
      out = [...out].sort((a, b) => {
        const da = daysUntil(a.deadline); const db = daysUntil(b.deadline);
        if (da === null && db === null) return 0;
        if (da === null) return 1;
        if (db === null) return -1;
        return da - db;
      });
    } else if (sortBy === "agency") {
      out = [...out].sort((a, b) => (a.agency || "").localeCompare(b.agency || ""));
    } else if (sortBy === "amount") {
      out = [...out].sort((a, b) => parseAmtToInr(b.amount) - parseAmtToInr(a.amount));
    }
    return out;
  })();

  const indiaCount = items.filter(it => (it.geography || "").toLowerCase().includes("india")).length;
  const intlCount = items.length - indiaCount;

  return (
    <div className="max-w-6xl mx-auto p-8">
      <div className="mb-8 flex items-start justify-between gap-6">
        <div>
          <div className="overline">Discover</div>
          <h1 className="font-display text-5xl mt-2">Funding feed</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">Fresh opportunities loaded instantly from our India-first curated library. Filter, sort or hit <b>Refresh live</b> for the AI scout to enrich with just-announced calls. Save any as an <b>alert</b> and we'll surface it as the deadline approaches.</p>
        </div>
        {alerts.length >= 0 && (
          <div className="flex gap-2">
            <Link to="/app/analyzer">
              <Button variant="outline" className="rounded-full" data-testid="btn-open-analyzer">
                <ScanSearch className="w-4 h-4 mr-2"/>Analyse notification
              </Button>
            </Link>
            {alerts.length > 0 && (
              <Link to="/app/alerts">
                <Button variant="outline" className="rounded-full" data-testid="btn-open-alerts">
                  <BellRing className="w-4 h-4 mr-2"/>My alerts ({alerts.length})
                </Button>
              </Link>
            )}
          </div>
        )}
      </div>

      <Tabs defaultValue="grants" className="mb-8">
        <TabsList>
          <TabsTrigger value="grants" data-testid="funding-tab-grants"><Compass className="w-4 h-4 mr-1"/>Grants & Funding</TabsTrigger>
          <TabsTrigger value="watchlist" data-testid="funding-tab-watchlist"><BellRing className="w-4 h-4 mr-1"/>Grant Alerts</TabsTrigger>
          <TabsTrigger value="consultancy" data-testid="funding-tab-consultancy"><Briefcase className="w-4 h-4 mr-1"/>Consultancy Works</TabsTrigger>
        </TabsList>
        <TabsContent value="grants">
      {/* Filters — instant curated feed, no LLM wait */}
      <div className="card-crisp rounded-lg p-5 mb-4 grid md:grid-cols-6 gap-3 items-end">
        <div className="space-y-2 md:col-span-1">
          <Label>Discipline</Label>
          <Select value={q.discipline} onValueChange={(v)=>setQ({...q, discipline:v})}>
            <SelectTrigger data-testid="funding-discipline"><SelectValue/></SelectTrigger>
            <SelectContent>{DISCIPLINES.map(d=><SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-2 md:col-span-1">
          <Label>Geography</Label>
          <Select value={q.geography} onValueChange={(v)=>setQ({...q, geography:v})}>
            <SelectTrigger data-testid="funding-geography"><SelectValue/></SelectTrigger>
            <SelectContent>{GEOGRAPHIES.map(g=><SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-2 md:col-span-1">
          <Label>Keywords</Label>
          <Input placeholder="e.g., quantum, climate…" value={q.keywords} onChange={(e)=>setQ({...q, keywords: e.target.value})} data-testid="funding-keywords"/>
        </div>
        <div className="space-y-2 md:col-span-1">
          <Label>Model</Label>
          <Select value={String(q.modelIdx)} onValueChange={(v)=>setQ({...q, modelIdx: Number(v)})}>
            <SelectTrigger data-testid="funding-model"><SelectValue/></SelectTrigger>
            <SelectContent>{MODELS.map((m,i)=><SelectItem key={i} value={String(i)}>{m.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-2 md:col-span-1">
          <Label>Sort</Label>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger data-testid="funding-sort"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="default">India first</SelectItem>
              <SelectItem value="deadline">Closest deadline</SelectItem>
              <SelectItem value="amount">Largest grant</SelectItem>
              <SelectItem value="agency">Agency A→Z</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={refreshLive} disabled={refreshing} className="rounded-full" data-testid="funding-search-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`}/>{refreshing ? "Refreshing…" : "Refresh live"}
        </Button>
      </div>

      {/* Text filter + geography tabs */}
      <div className="mb-3 flex flex-col md:flex-row md:items-center gap-3 justify-between">
        <div className="flex gap-2 flex-wrap">
          <button onClick={()=>setGeoTab("all")}   className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded-full border ${geoTab==="all"   ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary/50"}`} data-testid="geo-all">All ({items.length})</button>
          <button onClick={()=>setGeoTab("india")} className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded-full border ${geoTab==="india" ? "bg-success text-white border-success" : "bg-card border-border hover:border-success/50"}`} data-testid="geo-india">India ({indiaCount})</button>
          <button onClick={()=>setGeoTab("intl")}  className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded-full border ${geoTab==="intl"  ? "bg-accent  text-white border-accent"  : "bg-card border-border hover:border-accent/50"}`}  data-testid="geo-intl">International ({intlCount})</button>
          <button onClick={()=>setShowFilters(!showFilters)} className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded-full border ml-2 inline-flex items-center gap-1 ${showFilters ? "bg-foreground text-background border-foreground" : "bg-card border-border hover:border-primary/50"}`} data-testid="toggle-filters">
            <SlidersHorizontal className="w-3.5 h-3.5"/>More filters{categorySel.size ? ` · ${categorySel.size}` : ""}
          </button>
        </div>
        <div className="relative md:w-96">
          <Search className="w-4 h-4 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2"/>
          <Input value={filter} onChange={(e)=>setFilter(e.target.value)} placeholder="Filter this feed — SERB, climate, DBT…" className="pl-8" data-testid="funding-filter"/>
        </div>
      </div>

      {/* Advanced filters panel */}
      {showFilters && (
        <div className="card-crisp rounded-lg p-4 mb-4 space-y-4" data-testid="funding-advanced">
          <div className="flex flex-wrap gap-2 items-center">
            <span className="overline mr-1">Category</span>
            {CATEGORIES.map(c => {
              const on = categorySel.has(c);
              return (
                <button key={c} onClick={()=>toggleCat(c)} className={`text-[11px] uppercase tracking-widest px-2.5 py-1 rounded-full border ${on ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary/50"}`} data-testid={`cat-${c.toLowerCase().replace(/[^a-z]+/g,"-")}`}>
                  {c}
                </button>
              );
            })}
            {categorySel.size > 0 && <button onClick={()=>setCategorySel(new Set())} className="text-[11px] text-muted-foreground hover:text-primary ml-1">clear</button>}
          </div>
          <div className="flex flex-wrap gap-6 items-center">
            <div>
              <span className="overline mr-2">Funder scope</span>
              <div className="inline-flex gap-1">
                {["all","public","private"].map(k => (
                  <button key={k} onClick={()=>setFunderScope(k)} className={`text-xs px-3 py-1 rounded-full border capitalize ${funderScope===k ? "bg-accent text-white border-accent" : "bg-card border-border hover:border-accent/50"}`} data-testid={`scope-${k}`}>{k === "all" ? "All" : k}</button>
                ))}
              </div>
            </div>
            <div>
              <span className="overline mr-2">Deadline</span>
              <div className="inline-flex gap-1">
                {[["all","Any"],["open","Open"],["closing_soon","Closing ≤ 30d"]].map(([k, lbl]) => (
                  <button key={k} onClick={()=>setDeadlineFilter(k)} className={`text-xs px-3 py-1 rounded-full border ${deadlineFilter===k ? "bg-success text-white border-success" : "bg-card border-border hover:border-success/50"}`} data-testid={`dl-${k}`}>{lbl}</button>
                ))}
              </div>
            </div>
            <div>
              <span className="overline mr-2">Currency</span>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger className="w-24 inline-flex" data-testid="funding-currency"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="INR">INR ₹</SelectItem>
                  <SelectItem value="USD">USD $</SelectItem>
                  <SelectItem value="EUR">EUR €</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="overline">Budget range</span>
              <span className="text-xs text-muted-foreground">{inrToDisplay(budgetRange[0])} — {inrToDisplay(budgetRange[1])}</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px]">Min (INR)</Label>
                <Input type="number" min="0" step="100000" value={budgetRange[0]} onChange={(e)=>setBudgetRange([Number(e.target.value)||0, budgetRange[1]])} data-testid="budget-min"/>
              </div>
              <div>
                <Label className="text-[11px]">Max (INR)</Label>
                <Input type="number" min="0" step="1000000" value={budgetRange[1]} onChange={(e)=>setBudgetRange([budgetRange[0], Number(e.target.value)||0])} data-testid="budget-max"/>
              </div>
            </div>
            <div className="mt-2 flex flex-wrap gap-1">
              {[["₹1 L", 100000, 10000000],["₹10 L", 1000000, 50000000],["₹1 Cr+", 10000000, 1000000000],["₹100 Cr", 100000000, 10000000000]].map(([lbl,lo,hi]) => (
                <button key={lbl} onClick={()=>setBudgetRange([lo, hi])} className="text-[10px] px-2 py-0.5 rounded-full border border-border hover:border-primary/50" data-testid={`budget-preset-${lbl}`}>{lbl}</button>
              ))}
              <button onClick={()=>setBudgetRange([0, 1_000_000_000])} className="text-[10px] px-2 py-0.5 rounded-full border border-border text-muted-foreground hover:border-primary/50">clear</button>
            </div>
          </div>
        </div>
      )}

      <div className="mb-4 text-xs text-muted-foreground">
        Showing <b>{visible.length}</b> of <b>{items.length}</b> opportunities · {items.filter(x=>x.source==="live").length} fresh from live scout · {items.filter(x=>x.source==="curated"||!x.source).length} from GrantForge curated library
      </div>

      {loading && (
        <div className="grid md:grid-cols-2 gap-4">
          {[0,1,2,3].map(i=>(
            <div key={i} className="card-crisp rounded-lg p-5 animate-pulse">
              <div className="h-6 w-2/3 bg-secondary rounded mb-3"/>
              <div className="h-4 w-1/3 bg-secondary rounded mb-2"/>
              <div className="h-3 w-full bg-secondary rounded mt-4"/>
              <div className="h-3 w-4/5 bg-secondary rounded mt-2"/>
            </div>
          ))}
        </div>
      )}

      {!loading && visible.length === 0 && (
        <div className="card-crisp rounded-lg p-10 text-center">
          <Compass className="w-10 h-10 text-primary mx-auto mb-3"/>
          <div className="font-display text-2xl">No matches for that filter</div>
          <div className="text-muted-foreground mt-1">Try clearing the search box, changing the Geography tab, or hitting <b>Refresh live</b>.</div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {visible.map((it) => {
          const dLeft = daysUntil(it.deadline);
          const saved = alerts.some(a => a.name === it.name && a.agency === it.agency);
          const isLive = it.source === "live";
          const isIndia = (it.geography || "").toLowerCase().includes("india");
          return (
            <div key={it.id} className={`card-crisp rounded-lg p-5 hover:border-primary/60 transition-colors cursor-pointer ${isIndia ? "border-l-4 border-l-success" : ""}`} onClick={()=>setDetailItem(it)} data-testid={`fund-item-${it.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="overline">{it.agency}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full uppercase tracking-widest ${isLive ? "bg-primary/10 text-primary" : "bg-secondary text-muted-foreground"}`} title={isLive ? "Fresh from live scout" : "From GrantForge curated library"}>
                      {isLive ? "Live" : "Curated"}
                    </span>
                  </div>
                  <div className="font-display text-2xl leading-tight">{it.name}</div>
                </div>
                {it.url && (
                  <a href={it.url} target="_blank" rel="noreferrer noopener" onClick={(e)=>e.stopPropagation()} className="text-primary hover:underline text-sm flex items-center gap-1 shrink-0" data-testid={`fund-visit-${it.id}`}>Visit <ExternalLink className="w-3 h-3"/></a>
                )}
              </div>
              <div className="mt-3 text-sm text-muted-foreground">{it.summary}</div>
              <div className="mt-4 flex flex-wrap gap-2 text-xs">
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${isIndia ? "bg-success/10 text-success" : "bg-secondary"}`}><Globe className="w-3 h-3"/>{it.geography}</span>
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${dLeft !== null && dLeft <= 30 ? "bg-red-100 text-red-800" : "bg-secondary"}`}>
                  <CalendarClock className="w-3 h-3"/>{it.deadline || "TBD"}{dLeft !== null && dLeft >= 0 ? ` · ${dLeft}d` : ""}
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-secondary"><Coins className="w-3 h-3"/>{it.amount || "Variable"}</span>
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-secondary">{it.discipline}</span>
                {it.category && <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary/10 text-primary">{it.category}</span>}
                {it.funder_scope && <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-accent/10 text-accent">{it.funder_scope}</span>}
              </div>
              <div className="mt-4 flex gap-2" onClick={(e)=>e.stopPropagation()}>
                <Button size="sm" variant={saved ? "outline" : "default"} onClick={()=>saveAlert(it)} className="rounded-full" data-testid={`fund-save-${it.id}`}>
                  <Bell className="w-3.5 h-3.5 mr-1"/>{saved ? "Alert added" : "Save as alert"}
                </Button>
                <Button size="sm" variant="ghost" onClick={()=>setDetailItem(it)} className="rounded-full" data-testid={`fund-open-${it.id}`}>
                  Open details
                </Button>
              </div>
            </div>
          );
        })}
      </div>
      <NotificationDetail item={detailItem} open={!!detailItem} onClose={()=>setDetailItem(null)} onSaveAlert={(it)=>{ saveAlert(it); setDetailItem(null); }}/>
        </TabsContent>
        <TabsContent value="watchlist">
          <WatchListPanel/>
        </TabsContent>
        <TabsContent value="consultancy">
          <ConsultancyOpsPanel/>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function WatchListPanel() {
  const [watches, setWatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ label:"", keywords:"", geography:"India", discipline:"Sciences", include_scholar:false });
  const [runningId, setRunningId] = useState("");
  const [hits, setHits] = useState({});   // id → items
  const [showNew, setShowNew] = useState({}); // id → new_count

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/watchlist"); setWatches(data || []); }
    catch { toast.error("Could not load alerts"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.keywords.trim()) return toast.error("Add at least one keyword");
    try {
      await api.post("/watchlist", form);
      setForm({ label:"", keywords:"", geography:"India", discipline:"Sciences", include_scholar:false });
      toast.success("Alert created");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create alert"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this alert?")) return;
    try { await api.delete(`/watchlist/${id}`); load(); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };
  const runNow = async (id) => {
    setRunningId(id);
    try {
      const { data } = await api.post(`/watchlist/${id}/run`);
      setHits(prev => ({ ...prev, [id]: data.items || [] }));
      setShowNew(prev => ({ ...prev, [id]: data.new_count || 0 }));
      toast.success(`${data.new_count || 0} new hit${(data.new_count||0)===1?"":"s"} · ${data.items?.length||0} total`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Run failed"); }
    finally { setRunningId(""); }
  };

  return (
    <div className="space-y-6">
      <div className="card-crisp rounded-lg p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="overline">Google-Alerts style</div>
            <div className="font-display text-2xl">Grant alerts on autopilot</div>
            <div className="text-sm text-muted-foreground mt-1">Add a keyword + geography + discipline. Hit <b>Run now</b> to fetch matching live grant calls (with Google Scholar hits if enabled). We store what we've already surfaced so new grants stand out.</div>
          </div>
        </div>
        <div className="grid md:grid-cols-6 gap-3 items-end">
          <div className="md:col-span-2"><Label>Keywords</Label><Input placeholder="e.g., CRISPR, gene therapy, retinal" value={form.keywords} onChange={(e)=>setForm({...form, keywords:e.target.value})} data-testid="wl-keywords"/></div>
          <div><Label>Geography</Label>
            <Select value={form.geography} onValueChange={(v)=>setForm({...form, geography:v})}>
              <SelectTrigger data-testid="wl-geography"><SelectValue/></SelectTrigger>
              <SelectContent>{GEOGRAPHIES.map(g=><SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Discipline</Label>
            <Select value={form.discipline} onValueChange={(v)=>setForm({...form, discipline:v})}>
              <SelectTrigger data-testid="wl-discipline"><SelectValue/></SelectTrigger>
              <SelectContent>{DISCIPLINES.map(d=><SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Label (optional)</Label><Input placeholder="My CRISPR watch" value={form.label} onChange={(e)=>setForm({...form, label:e.target.value})} data-testid="wl-label"/></div>
          <Button onClick={create} className="rounded-full" data-testid="wl-create"><Plus className="w-4 h-4 mr-1"/>Add alert</Button>
          <label className="text-xs flex items-center gap-2 col-span-2">
            <input type="checkbox" checked={form.include_scholar} onChange={(e)=>setForm({...form, include_scholar:e.target.checked})} data-testid="wl-scholar"/>
            Also fetch recent papers (OpenAlex / Google Scholar-style)
          </label>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading alerts…</div>
      ) : watches.length === 0 ? (
        <div className="card-crisp rounded-lg p-10 text-center">
          <BellRing className="w-10 h-10 text-primary mx-auto mb-3"/>
          <div className="font-display text-2xl">No alerts yet</div>
          <div className="text-muted-foreground mt-1 max-w-lg mx-auto">Set up your first alert above — we'll surface new grants that match, straight in this tab.</div>
        </div>
      ) : (
        <div className="space-y-4">
          {watches.map(w => (
            <div key={w.id} className="card-crisp rounded-lg p-5" data-testid={`wl-item-${w.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-display text-xl truncate">{w.label || w.keywords}</div>
                  <div className="text-xs text-muted-foreground mt-1">Keywords: <b>{w.keywords}</b> · {w.geography} · {w.discipline || "any discipline"} {w.include_scholar && <span className="text-primary">· Scholar</span>}</div>
                  <div className="text-[11px] text-muted-foreground mt-1">
                    {w.last_run_at ? `Last run ${new Date(w.last_run_at).toLocaleString()} · ${w.last_hit_count || 0} hits` : "Never run — hit Run now"}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" variant="outline" onClick={()=>runNow(w.id)} disabled={runningId===w.id} data-testid={`wl-run-${w.id}`}>
                    {runningId===w.id ? <Loader2 className="w-4 h-4 animate-spin"/> : <><RefreshCw className="w-4 h-4 mr-1"/>Run now</>}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={()=>del(w.id)} data-testid={`wl-del-${w.id}`}><Trash2 className="w-4 h-4 text-destructive"/></Button>
                </div>
              </div>
              {(hits[w.id] || []).length > 0 && (
                <div className="mt-3 border-t border-border pt-3 space-y-2">
                  {showNew[w.id] > 0 && (
                    <div className="text-xs bg-emerald-100 text-emerald-900 rounded px-2 py-1 inline-block">
                      🆕 {showNew[w.id]} new since last run
                    </div>
                  )}
                  {hits[w.id].slice(0, 8).map((it, i) => (
                    <div key={i} className={`text-sm flex items-start gap-2 p-2 rounded ${it.is_new ? "bg-emerald-50 border border-emerald-200" : ""}`}>
                      <BellRing className={`w-4 h-4 mt-0.5 shrink-0 ${it.is_new ? "text-emerald-700" : "text-muted-foreground"}`}/>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">{it.name}</div>
                        <div className="text-xs text-muted-foreground">{it.agency} · {it.geography} · {it.deadline || "Rolling"}</div>
                      </div>
                      {it.url && <a href={it.url} target="_blank" rel="noreferrer" className="text-primary text-xs hover:underline"><ExternalLink className="w-3 h-3 inline"/></a>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ConsultancyOpsPanel() {  const [q, setQ] = useState({ discipline: "Sciences", geography: "India", keywords: "", modelIdx: 0 });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const search = async () => {
    setLoading(true); setItems([]);
    const tid = toast.loading("Scouting consultancy opportunities…");
    try {
      const m = MODELS[q.modelIdx];
      const { data } = await api.post("/consultancy/search", {
        discipline: q.discipline, geography: q.geography, keywords: q.keywords,
        model_provider: m.provider, model_name: m.name,
      });
      setItems(data.items || []);
      toast.dismiss(tid);
      if (data.warning) toast.warning(data.warning);
      else toast.success(`Found ${data.items?.length || 0} opportunities`);
    } catch (e) { toast.dismiss(tid); toast.error("Search failed"); }
    finally { setLoading(false); }
  };
  return (
    <div>
      <div className="card-crisp rounded-lg p-5 mb-8 grid md:grid-cols-5 gap-3 items-end">
        <div className="space-y-2"><Label>Discipline</Label>
          <Select value={q.discipline} onValueChange={(v)=>setQ({...q, discipline:v})}>
            <SelectTrigger data-testid="cons-search-discipline"><SelectValue/></SelectTrigger>
            <SelectContent>{DISCIPLINES.map(d=><SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
          </Select></div>
        <div className="space-y-2"><Label>Geography</Label>
          <Select value={q.geography} onValueChange={(v)=>setQ({...q, geography:v})}>
            <SelectTrigger data-testid="cons-search-geo"><SelectValue/></SelectTrigger>
            <SelectContent>{GEOGRAPHIES.map(g=><SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
          </Select></div>
        <div className="space-y-2"><Label>Model</Label>
          <Select value={String(q.modelIdx)} onValueChange={(v)=>setQ({...q, modelIdx:Number(v)})}>
            <SelectTrigger><SelectValue/></SelectTrigger>
            <SelectContent>{MODELS.map((m,i)=><SelectItem key={i} value={String(i)}>{m.label}</SelectItem>)}</SelectContent>
          </Select></div>
        <div className="space-y-2"><Label>Keywords</Label>
          <Input placeholder="e.g., PSU, review panel…" value={q.keywords} onChange={(e)=>setQ({...q, keywords:e.target.value})} data-testid="cons-search-keywords"/></div>
        <Button onClick={search} disabled={loading} className="rounded-full" data-testid="cons-search-btn">
          <Search className="w-4 h-4 mr-2"/>{loading ? "Searching…" : "Find consultancy works"}
        </Button>
      </div>
      {loading && (
        <div className="grid md:grid-cols-2 gap-4">{[0,1,2,3].map(i=>(<div key={i} className="card-crisp rounded-lg p-5 animate-pulse"><div className="h-6 w-2/3 bg-secondary rounded mb-3"/><div className="h-3 w-full bg-secondary rounded mt-3"/><div className="h-3 w-4/5 bg-secondary rounded mt-2"/></div>))}</div>
      )}
      {!loading && items.length===0 && (
        <div className="card-crisp rounded-lg p-10 text-center">
          <Briefcase className="w-10 h-10 text-primary mx-auto mb-3"/>
          <div className="font-display text-2xl">Find paid consultancy work</div>
          <p className="text-muted-foreground mt-1 max-w-lg mx-auto">Empanelments, expert-panel calls, technical advisory, and industry R&D consulting — filtered by your discipline and region.</p>
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-4">
        {items.map(it => (
          <div key={it.id} className="card-crisp rounded-lg p-5 hover:border-primary/60 transition-colors" data-testid={`cons-op-${it.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="overline flex items-center gap-1"><Building2 className="w-3 h-3"/>{it.agency}</div>
                <div className="font-display text-2xl mt-1 leading-tight">{it.title}</div>
              </div>
              {it.link && <a href={it.link} target="_blank" rel="noreferrer" className="text-primary hover:underline text-sm flex items-center gap-1 shrink-0" data-testid={`cons-op-link-${it.id}`}>Apply <ExternalLink className="w-3 h-3"/></a>}
            </div>
            <p className="text-sm text-muted-foreground mt-3">{it.summary}</p>
            <div className="mt-4 flex flex-wrap gap-2 text-xs">
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-secondary"><Globe className="w-3 h-3"/>{it.geography}</span>
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-secondary">{it.discipline}</span>
              {it.fee_range && <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-secondary"><Coins className="w-3 h-3"/>{it.fee_range}</span>}
              {it.engagement && <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary/10 text-primary">{it.engagement}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
